<template>
  <header class="page-header" :class="headerClasses">
    <div class="page-header__title-row">
      <div v-if="showBack" class="page-header__back-slot">
        <!-- One back button for every route: the filled disc, with no variant to
             choose, so no page can draw its way back differently. -->
        <BackButton
          class="page-header__back"
          variant="filled"
          :label="backLabel"
          :aria-label="backLabel"
          @click="$emit('back')"
        />
      </div>
      <div class="page-header__heading">
        <h1 class="page-header__title" :class="titleClasses">
          <button
            v-if="titleAs === 'button'"
            type="button"
            class="page-header__title-button tap-44 tap-44--from-start"
            :aria-label="titleButtonLabel || null"
            @click="$emit('title')"
          >
            <span class="page-header__title-text"><slot name="title" :title="title">{{ title }}</slot></span>
            <i class="pi pi-chevron-down page-header__title-chevron" aria-hidden="true" />
          </button>
          <slot v-else name="title" :title="title">{{ title }}</slot>
        </h1>
        <div v-if="subtitle" class="page-header__subtitle">{{ subtitle }}</div>
      </div>

      <div v-if="actions.length || $slots.actions" class="page-header__actions">
        <button
          v-for="action in actions"
          :key="action.key"
          type="button"
          class="page-header__action tap-44"
          :class="{ 'page-header__action--pressed': action.pressed === true }"
          :aria-label="action.label"
          :aria-pressed="action.pressed == null ? null : String(action.pressed)"
          @click="$emit('action', action.key)"
        >
          <i class="pi" :class="`pi-${action.icon}`" aria-hidden="true" />
        </button>
        <slot name="actions" />
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed } from 'vue';

import BackButton from '../BackButton/BackButton.vue';

// The one top-of-page header. Every page that has a title at its top uses it,
// and configures it through the props below rather than through its own CSS: a
// page that needs the header to behave differently asks for a prop.
//
// One row: the back button when `showBack`, then the title and its subtitle,
// then the actions at the end. The back button and the actions sit level with
// the title's first line, so a wrapped title and the subtitle run on beneath
// the title alone.
//
// Slots:
//   actions — a control the header cannot draw from the `actions` prop: a
//     component with its own state, such as the dealership chip or the follow
//     heart. It renders after the prop's buttons, in the same cluster. No
//     scope; read at render time, so a page may reveal or withdraw it.
//   title — the heading's content, scoped `{ title }`. The <h1> stays the
//     component's, so a page can decorate its words (a count, a glyph) without
//     taking the one heading over. With `titleAs="button"` the slot renders
//     inside the button, before its chevron.
defineOptions({ name: 'PageHeader' });

const props = defineProps({
  // The page's <h1>, and the only heading this component renders. `required`
  // is satisfied by an empty string, which would render a heading with no
  // accessible name — the one way "every page opens with a title" fails
  // silently — so the validator rejects a blank one in development.
  title: { type: String, required: true, validator: (value) => value.trim() !== '' },
  subtitle: { type: String, default: null },
  // Pages that are reached from a parent (vehicle detail, account screens)
  // set this so the dealer can navigate back. Tab roots leave it false.
  showBack: { type: Boolean, default: false },
  // The back button's accessible name — the one string this component owns, so
  // the page translates it the way Sheet's `closeLabel` is translated.
  backLabel: { type: String, default: 'Back' },
  // `button` makes the title itself a control — the Sales tab's sale switcher,
  // where the title names the sale and a tap opens Find a sale. It stays inside
  // the <h1> so the page keeps its heading, gains a chevron that says it opens
  // something, and emits `title`. One line, ellipsized: a sale name can be long
  // and the control must not wrap into a two-line tap target.
  titleAs: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'button'].includes(value),
  },
  // The title button's accessible name when the visible words are not enough
  // ("Switch sale, Coastal Simulcast"). Null reads the visible title.
  titleButtonLabel: { type: String, default: null },
  // How the title takes a line that is short of room:
  //   `wrap` — it wraps at its words first, and the actions narrow only after
  //            that, so an action that can grow long has to truncate itself;
  //   `one`  — it keeps one line, and the actions take what is left. Bid Stack's
  //            two-word title next to a long dealership name.
  titleLines: {
    type: String,
    default: 'wrap',
    validator: (value) => ['wrap', 'one'].includes(value),
  },
  // The page's icon buttons, at the title row's end: `[{ key, icon, label,
  // pressed? }]`. `icon` is a PrimeIcons name without its `pi-` prefix, `label`
  // is the button's accessible name, already translated. `pressed` makes it a
  // toggle — true or false sets `aria-pressed` and true fills the button; leave
  // it out for a plain button. A press emits `action` with the button's `key`.
  actions: {
    type: Array,
    default: () => [],
    validator: (value) => value.every((action) => Boolean(action?.key && action.icon && action.label)),
  },
  // How far the header sits from the top of the screen:
  //   `safe-area-and-gap` — clear of the notch, then 16px more. A header at the
  //                         top of a page, which is nearly every page.
  //   `safe-area-or-gap`  — the notch inset or 16px, whichever is larger: a
  //                         header that must sit flush to the top of the web
  //                         view, as Search's collapsing bar does.
  //   `none`              — no top or side padding: the header sits inside a
  //                         scroller that already clears the notch and pads its
  //                         own sides, as Bid Stack's does.
  inset: {
    type: String,
    default: 'safe-area-and-gap',
    validator: (value) => ['safe-area-and-gap', 'safe-area-or-gap', 'none'].includes(value),
  },
});

const headerClasses = computed(() => [
  `page-header--inset-${props.inset}`,
  {
    'page-header--with-back': props.showBack,
    'page-header--title-one-line': props.titleLines === 'one',
    'page-header--title-button': props.titleAs === 'button',
  },
]);

const titleClasses = computed(() => ({ 'page-header__title--button': props.titleAs === 'button' }));

// `back` says the dealer asked to go back, and nothing else: where that goes is
// the page's to decide. A library component may not read or push router state.
// `title` is the title button's press (only with `titleAs="button"`), and
// `action` an action button's press, carrying its `key`; like `back`, what they
// do is the page's.
defineEmits(['back', 'title', 'action']);
</script>

<style scoped>
/* `flex-shrink: 0`: a page lays itself out as a column, and the header is never
   the part of it that gives way. */
.page-header {
  flex-shrink: 0;
  background: var(--p-page-header-background);
  color: var(--p-page-header-color);
  padding: calc(env(safe-area-inset-top) + 16px) 20px 16px;
  font-family: var(--p-page-header-font-family);
}

.page-header--inset-safe-area-or-gap {
  padding-top: max(16px, env(safe-area-inset-top));
}

.page-header--inset-none {
  padding: 0 0 16px;
}

/*
 * The title row. A grid rather than a flex row, for a long title or a long
 * action on a narrow phone: the heading's track never drops below its longest
 * word and takes the rest of the line, and the actions' track takes its natural
 * width out of what is left and gives way only after the title has wrapped. A
 * flex row shrinks both together, so it wraps the title and truncates the
 * actions at once while the wrapped title leaves room unused.
 */
.page-header__title-row {
  display: grid;
  grid-template-columns: minmax(min-content, 1fr) minmax(0, max-content);
  align-items: start;
  column-gap: var(--p-page-header-title-row-column-gap);
}

/* With a back button the row gains a leading track of the button's own width. */
.page-header--with-back .page-header__title-row {
  grid-template-columns: max-content minmax(min-content, 1fr) minmax(0, max-content);
}

/* The back button, level with the title's first line as the actions are: the
   slot is one title line tall and centers the button in it. */
.page-header__back-slot {
  display: flex;
  align-items: center;
  height: calc(var(--p-page-header-title-font-size) * var(--p-page-header-title-line-height));
}

.page-header__heading {
  min-width: 0;
}

/* A title button is one ellipsized line, so its smallest width is no width at
   all: the actions keep their natural width and the title cuts short with an
   ellipsis. Without this the heading's track is held at the button's full
   unwrapped text, and a long sale name pushes the actions off the screen. */
.page-header--title-button .page-header__title-row {
  grid-template-columns: minmax(0, 1fr) max-content;
}

.page-header--title-button.page-header--with-back .page-header__title-row {
  grid-template-columns: max-content minmax(0, 1fr) max-content;
}

/* Level with the title's first line: the cluster is exactly one title line
   tall and centers its controls in it, so a wrapped title runs on beneath them
   and a subtitle never pushes them down. */
.page-header__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--p-page-header-actions-gap);
  min-width: 0;
  height: calc(var(--p-page-header-title-font-size) * var(--p-page-header-title-line-height));
}

.page-header__action {
  flex: none;
  display: grid;
  place-items: center;
  width: var(--p-page-header-action-size);
  height: var(--p-page-header-action-size);
  padding: 0;
  border: var(--p-page-header-action-border-width) solid var(--p-page-header-action-border-color);
  border-radius: var(--p-page-header-action-border-radius);
  background: var(--p-page-header-action-background);
  color: var(--p-page-header-action-color);
  cursor: pointer;
}

.page-header__action i {
  font-size: var(--p-page-header-action-icon-font-size);
}

.page-header__action--pressed {
  background: var(--p-page-header-action-pressed-background);
  color: var(--p-page-header-action-pressed-color);
}

.page-header__action:focus-visible {
  outline: 2px solid var(--p-page-header-action-focus-outline-color);
  outline-offset: 2px;
}

.page-header__title {
  font-family: var(--p-page-header-title-font-family);
  font-weight: var(--p-page-header-title-font-weight);
  letter-spacing: var(--p-page-header-title-letter-spacing);
  font-size: var(--p-page-header-title-font-size);
  line-height: var(--p-page-header-title-line-height);
  margin: 0;
  color: var(--p-page-header-title-color);
}

/* One line: the heading's track is the full width of its words, so the
   actions' track takes only what is left and truncates there. */
.page-header--title-one-line .page-header__title {
  white-space: nowrap;
}

/* The title as a control: the heading's own type, no chrome, one line. */
.page-header__title--button {
  min-width: 0;
}

.page-header__title-button {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--p-page-header-title-button-gap);
  max-width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: left;
  cursor: pointer;
}

/* One title line is 26px, so the switcher ANSWERS across 44 out of the shared
   `tap-44` class on the element above, capped `--from-start`: the title's track
   sits between the back button and the actions, and an extender that grew both
   ways would reach back over the back button, which is an EARLIER sibling and
   would lose the press. Growing right only, it reaches into the column gap and
   under the actions, which are later siblings and win there. Its own 9px of
   reach over the subtitle takes no press from it: the subtitle is words, not a
   control. The rule used to be written out here; it is one mechanism now, in
   themes/src/variables.css. */

.page-header__title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-header__title-chevron {
  flex: none;
  font-size: var(--p-page-header-title-button-chevron-font-size);
  color: var(--p-page-header-title-button-chevron-color);
}

.page-header__subtitle {
  font-size: var(--p-page-header-subtitle-font-size);
  color: var(--p-page-header-subtitle-color);
  margin-top: 4px;
  line-height: var(--p-page-header-subtitle-line-height);
}
</style>
